import type { HoverSegment, Page, PointerKind, Stroke, Tool, UUID } from '../model/types';
import { DEFAULT_BUTTON_MAPPING, isEraserButtons, type ButtonMapping } from './input/buttons';
import { HoverRecorder } from './input/HoverRecorder';
import { PalmFilter } from './input/PalmFilter';
import { SampleRunBuilder, type RunContext } from './input/PenCapture';
import { coalesced, metaOf, predicted, toSample, type PointerMeta, type RawSample } from './input/samples';
import { PageModel } from './PageModel';
import { LiveLayer } from './render/LiveLayer';
import { TileCache } from './render/TileCache';
import { Stats } from './Stats';
import { Viewport } from './view/Viewport';

type Unrevved<T> = Omit<T, 'rev'>;

/** Where the engine persists data. The app uses IndexedDB; replay uses memory. */
export interface EngineStore {
  saveStroke(s: Unrevved<Stroke>): Promise<unknown>;
  journalStroke(s: Unrevved<Stroke>): Promise<unknown>;
  clearJournal(id: UUID): Promise<unknown>;
  saveHover(h: Unrevved<HoverSegment>): Promise<unknown>;
}

export interface ToolState {
  tool: Tool;
  color: string;
  baseWidth: number;
}

export interface EngineOptions {
  store: EngineStore;
  notebookId: UUID;
  deviceId: UUID;
  tool: ToolState;
  buttons?: ButtonMapping;
  onError?: (message: string) => void;
  onStrokeCommitted?: (s: Unrevved<Stroke>) => void;
  /** Low-latency (desynchronized) display canvas. Default true. */
  lowLatency?: boolean;
  /** Called when the zoom level changes (relative to fit-width). */
  onZoom?: (userZoom: number) => void;
}

const JOURNAL_EVERY_MS = 500;
const RASTER_BUDGET_MS = 8;
const ERASER_RING_PX = 14;
/** Re-raster at the new zoom this long after the last pinch/zoom change. */
const ZOOM_SETTLE_MS = 120;

interface PenState {
  pointerId: number;
  mode: 'ink' | 'erase';
  pageIdx: number;
  builder: SampleRunBuilder | null;
  style: ToolState;
  journalTimer: ReturnType<typeof setInterval> | null;
}

export class Engine {
  readonly stats = new Stats();
  readonly viewport = new Viewport();
  readonly surface: HTMLDivElement;
  private models: PageModel[] = [];
  private tiles = new TileCache();
  private live: LiveLayer;
  /** On-screen canvas: opaque, low-latency. Shows the back buffer plus the live stroke. */
  private display: HTMLCanvasElement;
  private dctx: CanvasRenderingContext2D;
  /** Off-screen buffer holding desk + pages + committed ink. */
  private back: OffscreenCanvas | HTMLCanvasElement;
  private cctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;
  private palm = new PalmFilter();
  private hover: HoverRecorder;
  private pen: PenState | null = null;
  private drag: { pointerId: number; lastX: number; lastY: number; lastT: number; vx: number; vy: number } | null = null;
  /** Accepted finger touches (viewport-relative CSS px). */
  private touches = new Map<number, { x: number; y: number }>();
  private pinch: { dist: number; mx: number; my: number } | null = null;
  private ignoredTouches = new Set<number>();
  private vx = 0;
  private vy = 0;
  private settleTimer: ReturnType<typeof setTimeout> | null = null;
  private raf = 0;
  private rect: DOMRect;
  private lastButtons = 0;
  private resizeObs: ResizeObserver;
  private tool: ToolState;
  private buttons: ButtonMapping;
  private deskColor = '#9a9aa2';
  private destroyed = false;

  constructor(container: HTMLElement, private opts: EngineOptions) {
    this.tool = { ...opts.tool };
    this.buttons = opts.buttons ?? DEFAULT_BUTTON_MAPPING;

    const surface = (this.surface = document.createElement('div'));
    surface.className = 'ink-surface';
    this.display = document.createElement('canvas');
    this.display.className = 'ink-layer';
    surface.append(this.display);
    container.append(surface);
    // One opaque canvas. A transparent desynchronized overlay renders black on Android.
    const dctx = this.display.getContext('2d', { alpha: false, desynchronized: opts.lowLatency !== false });
    if (!dctx) throw new Error('2D canvas unavailable');
    this.dctx = dctx;
    this.stats.desynchronized = Boolean(dctx.getContextAttributes?.().desynchronized);
    if (typeof OffscreenCanvas !== 'undefined') {
      this.back = new OffscreenCanvas(1, 1);
      this.cctx = this.back.getContext('2d', { alpha: false })!;
    } else {
      this.back = document.createElement('canvas');
      this.cctx = this.back.getContext('2d', { alpha: false })!;
    }
    this.live = new LiveLayer(dctx, (x, y, w, h) => this.restoreRegion(x, y, w, h));
    this.rect = surface.getBoundingClientRect();
    const desk = getComputedStyle(container).getPropertyValue('--desk').trim();
    if (desk) this.deskColor = desk;

    this.hover = new HoverRecorder({
      contextAt: (s) => {
        const idx = this.pageIndexAt(s);
        return idx < 0 ? null : this.runContext(idx, 'pen');
      },
      save: (h) => {
        this.stats.hoverSegments++;
        opts.store.saveHover(h).catch((e) => this.fail('Hover data not saved', e));
      },
      setTimer: (fn, ms) => setTimeout(fn, ms),
      clearTimer: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
    });

    const on = <K extends keyof HTMLElementEventMap>(t: K, fn: (e: HTMLElementEventMap[K]) => void, o?: AddEventListenerOptions) =>
      surface.addEventListener(t, fn as EventListener, o);
    on('pointerdown', (e) => this.onDown(e));
    on('pointermove', (e) => this.onMove(e));
    on('pointerup', (e) => this.onUp(e, false));
    on('pointercancel', (e) => this.onUp(e, true));
    on('pointerleave', (e) => this.onLeave(e));
    on('contextmenu', (e) => e.preventDefault());
    on('wheel', (e) => this.onWheel(e), { passive: false });
    addEventListener('pagehide', this.flush);
    document.addEventListener('visibilitychange', this.onVisibility);

    this.resizeObs = new ResizeObserver(() => this.resize());
    this.resizeObs.observe(surface);
    this.resize();
  }

  // ---------- public API ----------

  setPages(pages: Page[], strokes: (Stroke | Unrevved<Stroke>)[]): void {
    const byPage = new Map<UUID, (Stroke | Unrevved<Stroke>)[]>();
    for (const s of strokes) {
      const arr = byPage.get(s.pageId);
      if (arr) arr.push(s);
      else byPage.set(s.pageId, [s]);
    }
    this.models = pages.map((p) => {
      const m = new PageModel(p);
      for (const s of byPage.get(p.id) ?? []) m.put(s);
      return m;
    });
    this.viewport.setPages(pages);
    this.tiles.clear();
    this.tiles.configure(this.viewport.dpr, this.viewport.scale);
    this.updateStatsCounts();
    this.requestFrame();
  }

  setTool(t: ToolState): void {
    this.tool = { ...t };
    this.stats.mode = t.tool;
  }

  setButtonMapping(m: ButtonMapping): void {
    this.buttons = m;
  }

  /** Zoom relative to fit-width (1 = fit). */
  get zoomLevel(): number {
    return this.viewport.userZoom;
  }

  resetZoom(): void {
    this.viewport.resetZoom();
    this.settleZoom();
  }

  /** Zooms about the centre of the view. */
  zoomBy(factor: number): void {
    if (this.viewport.zoomAt(factor, this.viewport.width / 2, this.viewport.height / 2)) this.settleZoom();
  }

  scrollBy(dyCss: number): void {
    if (this.viewport.scrollBy(dyCss)) this.requestFrame();
  }

  scrollToPage(i: number): void {
    const p = this.viewport.pages[i];
    if (!p) return;
    this.viewport.scrollTo(p.top - 24);
    this.requestFrame();
  }

  /** Converts page units to client CSS px for page `i` (used by replay). */
  pageToClient(i: number, x: number, y: number): { clientX: number; clientY: number } {
    const o = this.viewport.originCss(i);
    const r = this.surface.getBoundingClientRect();
    return { clientX: r.left + o.x + x * this.viewport.zoom, clientY: r.top + o.y + y * this.viewport.zoom };
  }

  get pageCount(): number {
    return this.models.length;
  }

  destroy(): void {
    this.flush();
    this.destroyed = true;
    cancelAnimationFrame(this.raf);
    if (this.settleTimer !== null) clearTimeout(this.settleTimer);
    this.resizeObs.disconnect();
    removeEventListener('pagehide', this.flush);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.surface.remove();
  }

  // ---------- input core (also driven by replay) ----------

  penDown(meta: PointerMeta, samples: RawSample[]): void {
    const first = samples[0];
    if (!first || this.pen) return;
    if (this.settleTimer !== null || this.tiles.scale !== this.viewport.scale) this.settleZoom();
    const kind: PointerKind = meta.pointerType === 'mouse' ? 'mouse' : meta.pointerType === 'touch' ? 'touch' : 'pen';
    const pageIdx = this.pageIndexAt(first);
    if (pageIdx < 0) return;
    if (kind === 'pen' && isEraserButtons(meta.buttons, this.buttons)) {
      this.hover.onPenDown(null);
      this.pen = { pointerId: meta.pointerId, mode: 'erase', pageIdx, builder: null, style: this.tool, journalTimer: null };
      this.stats.mode = 'eraser (side button)';
      this.drawRing(first);
      return;
    }
    const builder = new SampleRunBuilder(this.runContext(pageIdx, kind));
    this.hover.onPenDown(builder.id);
    const pen: PenState = { pointerId: meta.pointerId, mode: 'ink', pageIdx, builder, style: { ...this.tool }, journalTimer: null };
    pen.journalTimer = setInterval(() => this.journal(pen), JOURNAL_EVERY_MS);
    this.pen = pen;
    this.stats.mode = this.tool.tool;
    this.addSamples(builder, samples);
    this.drawLive(pen, [], first.timeStamp);
  }

  penMove(meta: PointerMeta, samples: RawSample[], predictedSamples: RawSample[]): void {
    const pen = this.pen;
    if (!pen || pen.pointerId !== meta.pointerId || samples.length === 0) return;
    if (pen.mode === 'erase' || !pen.builder) {
      this.drawRing(samples[samples.length - 1]!);
      return;
    }
    this.addSamples(pen.builder, samples);
    const extra: number[] = [];
    for (const p of predictedSamples) pen.builder.project(p, extra);
    const drawMs = this.drawLive(pen, extra, samples[samples.length - 1]!.timeStamp);
    this.stats.onMove(samples.length, predictedSamples.length, drawMs, performance.now() - samples[0]!.timeStamp);
  }

  /** `last` is the pointerup sample (null on cancel). */
  penUp(meta: PointerMeta, last: RawSample | null): void {
    const pen = this.pen;
    if (!pen || pen.pointerId !== meta.pointerId) return;
    this.pen = null;
    if (pen.journalTimer) clearInterval(pen.journalTimer);
    this.stats.mode = this.tool.tool;
    if (pen.mode === 'erase' || !pen.builder) {
      this.live.clear();
      this.live.reset();
      return;
    }
    if (last) this.addSamples(pen.builder, [last]);
    const stroke = pen.builder.toStroke(pen.style);
    this.stats.onStrokeEnd();
    this.commit(pen.pageIdx, stroke);
    this.hover.onStrokeEnd(stroke.id, stroke.pageId);
    this.opts.store
      .saveStroke(stroke)
      .then(() => this.opts.store.clearJournal(stroke.id))
      .catch((e) => this.fail('Stroke not saved', e));
    this.opts.onStrokeCommitted?.(stroke);
  }

  penHover(samples: RawSample[]): void {
    this.stats.onHover();
    this.hover.onHover(samples);
  }

  penLeave(): void {
    this.hover.onLeave();
  }

  // ---------- DOM events ----------

  private onDown(e: PointerEvent): void {
    if (e.pointerType === 'touch') {
      if (this.pen || this.touches.size >= 2 || !this.palm.acceptTouch(e.timeStamp, e.width, e.height)) {
        this.ignoredTouches.add(e.pointerId);
        return;
      }
      this.rect = this.surface.getBoundingClientRect();
      const x = e.clientX - this.rect.left, y = e.clientY - this.rect.top;
      this.touches.set(e.pointerId, { x, y });
      this.surface.setPointerCapture(e.pointerId);
      this.vx = this.vy = 0;
      if (this.touches.size === 1) {
        this.drag = { pointerId: e.pointerId, lastX: x, lastY: y, lastT: e.timeStamp, vx: 0, vy: 0 };
      } else {
        this.drag = null;
        this.pinch = this.pinchState();
      }
      return;
    }
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();
    this.logButtons(e);
    if (e.pointerType === 'pen') {
      this.palm.notePen(e.timeStamp, true);
      // A palm that landed just before the pen must not keep scrolling or zooming.
      this.endTouches();
      this.vx = this.vy = 0;
    }
    this.rect = this.surface.getBoundingClientRect();
    this.surface.setPointerCapture(e.pointerId);
    this.penDown(metaOf(e), coalesced(e));
  }

  private onMove(e: PointerEvent): void {
    if (e.pointerType === 'touch') {
      const t = this.touches.get(e.pointerId);
      if (!t) return;
      if (this.palm.penActive(e.timeStamp)) { this.endTouches(); return; }
      t.x = e.clientX - this.rect.left;
      t.y = e.clientY - this.rect.top;
      const vp = this.viewport;
      if (this.pinch && this.touches.size === 2) {
        const p = this.pinch, n = this.pinchState();
        // Zoom about the old midpoint, then pan so that point follows the fingers.
        const zoomed = vp.zoomAt(n.dist / p.dist, p.mx, p.my);
        const panned = vp.panBy(p.mx - n.mx, p.my - n.my);
        this.pinch = n;
        if (zoomed) this.scheduleSettle();
        if (zoomed || panned) this.requestFrame();
        return;
      }
      const d = this.drag;
      if (!d || d.pointerId !== e.pointerId) return;
      const dx = d.lastX - t.x, dy = d.lastY - t.y;
      const dt = Math.max(1, e.timeStamp - d.lastT);
      d.vx = 0.8 * (dx / dt) + 0.2 * d.vx;
      d.vy = 0.8 * (dy / dt) + 0.2 * d.vy;
      d.lastX = t.x;
      d.lastY = t.y;
      d.lastT = e.timeStamp;
      if (vp.panBy(dx, dy)) this.requestFrame();
      return;
    }
    if (e.pointerType === 'pen') {
      this.palm.notePen(e.timeStamp, this.pen !== null);
      this.stats.pointer = {
        type: e.pointerType, pressure: e.pressure, tiltX: e.tiltX, tiltY: e.tiltY,
        button: e.button, buttons: e.buttons, width: e.width, height: e.height,
      };
    }
    this.logButtons(e);
    if (this.pen) {
      this.penMove(metaOf(e), coalesced(e), predicted(e));
    } else if (e.pointerType === 'pen' && e.buttons === 0) {
      this.penHover(coalesced(e));
    }
  }

  private onUp(e: PointerEvent, cancelled: boolean): void {
    if (e.pointerType === 'touch') {
      this.ignoredTouches.delete(e.pointerId);
      if (!this.touches.delete(e.pointerId)) return;
      if (this.pinch) {
        // Pinch over. Ignore the remaining finger so the view doesn't jump.
        this.pinch = null;
        this.endTouches();
        return;
      }
      const d = this.drag;
      if (d && d.pointerId === e.pointerId) {
        this.drag = null;
        if (!cancelled && e.timeStamp - d.lastT < 80) {
          this.vx = d.vx;
          this.vy = d.vy;
          this.requestFrame();
        }
      }
      return;
    }
    if (e.pointerType === 'pen') this.palm.notePen(e.timeStamp, false);
    this.logButtons(e);
    this.penUp(metaOf(e), cancelled ? null : toSample(e));
  }

  private onLeave(e: PointerEvent): void {
    if (e.pointerType === 'pen' && !this.pen) this.penLeave();
  }

  private onWheel(e: WheelEvent): void {
    e.preventDefault();
    if (this.pen) return;
    const k = e.deltaMode === 1 ? 16 : 1;
    if (e.ctrlKey) {
      const r = this.surface.getBoundingClientRect();
      if (this.viewport.zoomAt(Math.exp(-e.deltaY * k * 0.002), e.clientX - r.left, e.clientY - r.top)) {
        this.scheduleSettle();
        this.requestFrame();
      }
      return;
    }
    if (this.viewport.panBy(e.deltaX * k, e.deltaY * k)) this.requestFrame();
  }

  private pinchState(): { dist: number; mx: number; my: number } {
    const [a, b] = [...this.touches.values()] as [{ x: number; y: number }, { x: number; y: number }];
    return { dist: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)), mx: (a.x + b.x) / 2, my: (a.y + b.y) / 2 };
  }

  /** Stops tracking all fingers until they lift. */
  private endTouches(): void {
    for (const id of this.touches.keys()) this.ignoredTouches.add(id);
    this.touches.clear();
    this.drag = null;
    if (this.pinch) {
      this.pinch = null;
      this.scheduleSettle();
    }
  }

  private scheduleSettle(): void {
    if (this.settleTimer !== null) clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(() => this.settleZoom(), ZOOM_SETTLE_MS);
  }

  /** Switches tiles to the current zoom (old tiles stay as placeholders until replaced). */
  private settleZoom(): void {
    if (this.settleTimer !== null) clearTimeout(this.settleTimer);
    this.settleTimer = null;
    this.tiles.configure(this.viewport.dpr, this.viewport.scale);
    this.requestFrame();
    this.opts.onZoom?.(this.viewport.userZoom);
  }

  private logButtons(e: PointerEvent): void {
    if (e.pointerType !== 'pen') return;
    if (e.type === 'pointermove' && e.buttons === this.lastButtons) return;
    this.lastButtons = e.buttons;
    this.stats.logButtons(e.type, e.button, e.buttons, e.pointerType);
  }

  private onVisibility = (): void => {
    if (document.visibilityState === 'hidden') this.flush();
  };

  /** Persists anything in flight (in-progress stroke to the journal, open hover segment). */
  private flush = (): void => {
    if (this.pen?.mode === 'ink') this.journal(this.pen);
    this.hover.flush();
  };

  // ---------- capture helpers ----------

  private runContext(pageIdx: number, kind: PointerKind): RunContext {
    const o = this.viewport.originCss(pageIdx);
    return {
      pageId: this.models[pageIdx]!.page.id,
      notebookId: this.opts.notebookId,
      deviceId: this.opts.deviceId,
      pointerType: kind,
      view: { zoom: this.viewport.zoom, offsetX: this.rect.left + o.x, offsetY: this.rect.top + o.y },
      timeOrigin: performance.timeOrigin,
    };
  }

  private pageIndexAt(s: RawSample): number {
    return this.viewport.pageAt(s.clientX - this.rect.left, s.clientY - this.rect.top);
  }

  private addSamples(b: SampleRunBuilder, samples: RawSample[]): void {
    for (const s of samples) b.add(s);
    this.stats.onSamples(samples.map((s) => s.timeStamp));
  }

  private journal(pen: PenState): void {
    if (!pen.builder || pen.builder.count === 0) return;
    this.opts.store.journalStroke(pen.builder.toStroke(pen.style)).catch(() => { /* best effort */ });
  }

  private fail(what: string, e: unknown): void {
    console.error(what, e);
    this.opts.onError?.(`${what}: ${(e as Error)?.message ?? e}`);
  }

  // ---------- rendering ----------

  private drawLive(pen: PenState, extra: number[], _t: number): number {
    const b = pen.builder!;
    const i = pen.pageIdx;
    return this.live.draw(b.buf.view(), extra, pen.style, this.viewport.originDev(i), this.viewport.scale, this.models[i]!.page.size);
  }

  private drawRing(s: RawSample): void {
    const dpr = this.viewport.dpr;
    this.live.drawRing((s.clientX - this.rect.left) * dpr, (s.clientY - this.rect.top) * dpr, ERASER_RING_PX * dpr);
  }

  private commit(pageIdx: number, stroke: Unrevved<Stroke>): void {
    const m = this.models[pageIdx]!;
    const rs = m.put(stroke);
    this.tiles.invalidate(m.page.id, rs.bounds);
    // Re-raster affected visible tiles now so the live→committed handoff has no gap.
    this.composite(Infinity);
    this.live.reset();
    this.updateStatsCounts();
  }

  private updateStatsCounts(): void {
    const i = this.viewport.visiblePages()[0];
    this.stats.strokesOnPage = i === undefined ? 0 : this.models[i]!.visibleCount;
    this.stats.tiles = this.tiles.count;
  }

  private resize(): void {
    if (this.destroyed) return;
    this.rect = this.surface.getBoundingClientRect();
    const dpr = devicePixelRatio || 1;
    const w = this.surface.clientWidth, h = this.surface.clientHeight;
    this.viewport.setSize(w, h, dpr);
    const W = Math.round(w * dpr), H = Math.round(h * dpr);
    this.display.width = W;
    this.display.height = H;
    this.back.width = W;
    this.back.height = H;
    this.tiles.configure(dpr, this.viewport.scale);
    this.composite(Infinity);
  }

  private requestFrame(): void {
    if (this.raf || this.destroyed) return;
    this.raf = requestAnimationFrame(this.frame);
  }

  private frame = (): void => {
    this.raf = 0;
    this.stats.tick();
    if ((this.vx !== 0 || this.vy !== 0) && !this.drag) {
      const moved = this.viewport.panBy(this.vx * 16, this.vy * 16);
      this.vx *= 0.94;
      this.vy *= 0.94;
      if (!moved || Math.hypot(this.vx, this.vy) < 0.02) this.vx = this.vy = 0;
    }
    const complete = this.composite(performance.now() + RASTER_BUDGET_MS);
    if (!complete || this.vx !== 0 || this.vy !== 0) this.requestFrame();
  };

  /**
   * Draws desk, pages and cached tiles. Missing/stale tiles are rasterized
   * until `deadline`; returns false if some are still missing.
   */
  private composite(deadline: number): boolean {
    const vp = this.viewport;
    const ctx = this.cctx;
    const W = this.back.width, H = this.back.height;
    const T = this.tiles.size;
    // Mid-gesture the tiles are at the old zoom: draw them scaled by k.
    const k = vp.scale / this.tiles.scale;
    const settled = Math.abs(k - 1) < 1e-9;
    const TT = T * k;
    const u = this.tiles.unit;
    let complete = true;
    let rasterMs = 0;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = this.deskColor;
    ctx.fillRect(0, 0, W, H);

    for (const i of vp.visiblePages()) {
      const m = this.models[i]!;
      const o = vp.originDev(i);
      const pw = Math.round(m.page.size.width * vp.scale), ph = Math.round(m.page.size.height * vp.scale);
      ctx.fillStyle = 'rgba(0,0,0,0.18)';
      ctx.fillRect(o.x + 2, o.y + 3, pw, ph);
      ctx.fillStyle = '#fdfdfb';
      ctx.fillRect(o.x, o.y, pw, ph);

      const tx0 = Math.max(0, Math.floor(-o.x / TT)), tx1 = Math.floor((Math.min(W, o.x + pw) - 1 - o.x) / TT);
      const ty0 = Math.max(0, Math.floor(-o.y / TT)), ty1 = Math.floor((Math.min(H, o.y + ph) - 1 - o.y) / TT);
      for (let ty = ty0; ty <= ty1; ty++) {
        for (let tx = tx0; tx <= tx1; tx++) {
          const x0 = o.x + Math.round(tx * TT), y0 = o.y + Math.round(ty * TT);
          const x1 = o.x + Math.round((tx + 1) * TT), y1 = o.y + Math.round((ty + 1) * TT);
          let tile = this.tiles.get(m.page.id, tx, ty);
          if (!tile || !tile.valid) {
            if (performance.now() < deadline) {
              tile = this.tiles.raster(m, tx, ty);
              rasterMs += this.tiles.lastRasterMs;
            } else {
              complete = false;
            }
          }
          if (tile) {
            if (settled) ctx.drawImage(tile.canvas, x0, y0);
            else ctx.drawImage(tile.canvas, x0, y0, x1 - x0, y1 - y0);
            continue;
          }
          // Not rastered yet at this zoom: show the previous zoom's pixels, scaled.
          const old = this.tiles.staleTiles(m.page.id, { minX: tx * u, minY: ty * u, maxX: (tx + 1) * u, maxY: (ty + 1) * u });
          if (old.length === 0) continue;
          ctx.save();
          ctx.beginPath();
          ctx.rect(x0, y0, x1 - x0, y1 - y0);
          ctx.clip();
          for (const st of old) {
            const sz = st.unit * vp.scale;
            ctx.drawImage(st.tile.canvas, o.x + st.x * vp.scale, o.y + st.y * vp.scale, sz, sz);
          }
          ctx.restore();
        }
      }
    }
    if (complete) this.tiles.dropStale();
    if (rasterMs > 0) this.stats.rasterMs = rasterMs;
    this.blit();
    this.stats.onComposite();
    this.stats.tiles = this.tiles.count;
    if (complete && settled && performance.now() < deadline) this.prefetch(deadline);
    return complete;
  }

  /** Copies the whole back buffer to the screen and redraws any in-progress stroke on top. */
  private blit(): void {
    const d = this.dctx;
    d.setTransform(1, 0, 0, 1, 0, 0);
    d.globalAlpha = 1;
    d.drawImage(this.back, 0, 0);
    this.live.repaintAfterBlit(this.pen !== null);
  }

  /** Copies one device-pixel rect from the back buffer to the screen. */
  private restoreRegion(x: number, y: number, w: number, h: number): void {
    const W = this.back.width, H = this.back.height;
    const x0 = Math.max(0, Math.floor(x)), y0 = Math.max(0, Math.floor(y));
    const x1 = Math.min(W, Math.ceil(x + w)), y1 = Math.min(H, Math.ceil(y + h));
    if (x1 <= x0 || y1 <= y0) return;
    const d = this.dctx;
    d.setTransform(1, 0, 0, 1, 0, 0);
    d.globalAlpha = 1;
    d.drawImage(this.back, x0, y0, x1 - x0, y1 - y0, x0, y0, x1 - x0, y1 - y0);
  }

  /** Rasterizes tiles just outside the viewport so scrolling doesn't reveal blanks. */
  private prefetch(deadline: number): void {
    const vp = this.viewport;
    const T = this.tiles.size;
    const H = this.back.height, W = this.back.width;
    const margin = H * 0.75;
    for (const i of vp.visiblePages(margin / vp.dpr)) {
      const m = this.models[i]!;
      const o = vp.originDev(i);
      const pw = Math.round(m.page.size.width * vp.scale), ph = Math.round(m.page.size.height * vp.scale);
      const tx0 = Math.max(0, Math.floor(-o.x / T)), tx1 = Math.floor((Math.min(W, o.x + pw) - 1 - o.x) / T);
      const ty0 = Math.max(0, Math.floor((-margin - o.y) / T)), ty1 = Math.floor((Math.min(H + margin, o.y + ph) - 1 - o.y) / T);
      for (let ty = ty0; ty <= ty1; ty++) {
        for (let tx = tx0; tx <= tx1; tx++) {
          const tile = this.tiles.get(m.page.id, tx, ty);
          if (tile?.valid) continue;
          if (performance.now() >= deadline) { this.requestFrame(); return; }
          this.tiles.raster(m, tx, ty);
        }
      }
    }
  }
}
